<script setup lang="ts">
  import { Menu as ArkMenu } from '@ark-ui/vue';
  import { computed, ref, watch } from 'vue';
  import Button from '../Button/Button.vue';
  import { GLOBAL_OVERLAYS_TARGET } from '../config/global-overlays-target';
  import {
    dropdownContentClass,
    dropdownContentPaddingClass,
    dropdownContentResetClass,
    dropdownPositionerClass,
  } from '../Dropdown/Dropdown.css';
  import { DropdownPlacement } from '../Dropdown/Dropdown.types';
  import { menuListClass } from './DropdownMenu.css';
  import type {
    DropdownMenuEmits,
    DropdownMenuProps,
    DropdownMenuSlots,
  } from './DropdownMenu.types';

  const props = withDefaults(defineProps<DropdownMenuProps>(), {
    open: undefined,
    placement: DropdownPlacement.BOTTOM_START,
  });

  const emit = defineEmits<DropdownMenuEmits>();

  defineSlots<DropdownMenuSlots>();

  const triggerLabel = computed(() => props.trigger?.label);

  const triggerButtonProps = computed(() => {
    const { label, ...buttonProps } = props.trigger ?? {};
    return buttonProps;
  });

  const isOpen = ref(props.disabled ? false : (props.open ?? false));

  const onOpenChange = (details: { open: boolean }) => {
    if (details.open && props.disabled) return;
    isOpen.value = details.open;
    emit('update:open', details.open);
  };

  watch(
    () => [props.open, props.disabled] as const,
    ([open, disabled]) => {
      if (disabled) {
        isOpen.value = false;
        return;
      }
      if (open !== undefined) isOpen.value = open;
    },
  );

  const contentTestId = computed(
    () => `${props.testId || props.id || 'default'}-dropdown-content`,
  );
</script>

<template>
  <ArkMenu.Root
    :open="isOpen"
    :positioning="{ placement }"
    @open-change="onOpenChange"
  >
    <ArkMenu.Context v-slot="menu">
      <ArkMenu.Trigger as-child>
        <slot name="trigger" :is-open="menu.open">
          <Button v-bind="triggerButtonProps" :active="menu.open">
            {{ triggerLabel }}
          </Button>
        </slot>
      </ArkMenu.Trigger>
    </ArkMenu.Context>
    <Teleport
      defer
      :disabled="disableTeleport"
      :to="`.${GLOBAL_OVERLAYS_TARGET}`"
    >
      <ArkMenu.Positioner :class="dropdownPositionerClass">
        <ArkMenu.Content :class="dropdownContentResetClass">
          <div
            :data-test-id="contentTestId"
            :class="[dropdownContentClass, dropdownContentPaddingClass]"
          >
            <div :class="menuListClass">
              <slot />
            </div>
          </div>
        </ArkMenu.Content>
      </ArkMenu.Positioner>
    </Teleport>
  </ArkMenu.Root>
</template>
